import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { PortalNotice } from "@/components/app/patterns/portal-notice";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { orpcPublic } from "@/lib/orpc";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { CheckCircle2, Clock } from "@/lib/public-icons";
import { CameraCheck } from "./-components/camera-check";
import { CameraIntro } from "./-components/camera-intro";
import { CaptureDialog } from "./-components/capture-dialog";
import { awaitsBankAccount, ExhibitCard, type SignerBlock } from "./-components/exhibit-card";
import { LinkAchBankStep } from "./-components/link-ach-bank-step";
import { ReadingIntro } from "./-components/reading-intro";
import { SignerCard } from "./-components/signer-card";
import { SigningShell } from "./-components/signing-shell";
import { SigningSteps } from "./-components/signing-steps";
import { SigningNav } from "./-components/signing-nav";
import { StartSigningBar } from "./-components/start-signing-bar";
import { useLinkVisit, useReadingTrail } from "./-components/use-reading-trail";
import { useSigningPhase } from "./-components/use-signing-phase";

export const Route = createFileRoute("/admission-sign/$token")({
  component: AdmissionSigningPage,
});

const ROLE_LABELS: Record<string, string> = {
  resident: "the resident",
  responsible_party: "the responsible party",
  facility: "the community",
  witness: "a witness",
};

type Capture = { mode: "one"; sectionKey: string; block: SignerBlock } | { mode: "all" };

const blockKey = (sectionKey: string, fieldKey: string) => `${sectionKey}:${fieldKey}`;

/** The packet is read first; "Start signing" explains the photo, then opens the signature
 * lines. The server decides which forms and lines are theirs from the link, and refuses a
 * signature without ticked acknowledgments or a photo. */
function AdmissionSigningPage() {
  const { token } = Route.useParams();
  const queryClient = useQueryClient();
  const [signerName, setSignerName] = useState("");
  const [ticked, setTicked] = useState<Record<string, Set<string>>>({});
  const [capture, setCapture] = useState<Capture | null>(null);
  const { phase, goTo, stepHeading } = useSigningPhase(token);
  // Signing the last block can complete the packet, after which the link
  // stops resolving; remembering what was signed here shows a thank-you
  // instead of "this link is not valid".
  const [sessionSigned, setSessionSigned] = useState<Set<string>>(new Set());

  const visit = useLinkVisit();
  const page = useQuery({
    ...orpcPublic.care.admissions.public.open.queryOptions({ input: { token, ...visit } }),
    retry: false,
  });
  useReadingTrail(token, visit, page.data?.exhibits.map((exhibit) => exhibit.key) ?? []);

  const afterSigning = async (keys: string[], message: string) => {
    setSessionSigned((previous) => new Set([...previous, ...keys]));
    setCapture(null);
    toast.success(message);
    await invalidateKeys(queryClient, [orpcPublic.care.admissions.public.key()]);
  };

  const signOne = useMutation(
    orpcPublic.care.admissions.public.sign.mutationOptions({
      onSuccess: (_result, input) =>
        afterSigning([blockKey(input.sectionKey, input.fieldKey)], "Signed. Thank you."),
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );
  const signAll = useMutation(
    orpcPublic.care.admissions.public.signAll.mutationOptions({
      onSuccess: () =>
        afterSigning(
          // An ACH form still waiting on its bank account is left for after it.
          (page.data?.exhibits ?? [])
            .filter((exhibit) => !awaitsBankAccount(exhibit))
            .flatMap((exhibit) =>
              exhibit.blocks.map((block) => blockKey(exhibit.key, block.fieldKey)),
            ),
          "Your signature was applied to every form. Thank you.",
        ),
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  if (page.isPending) {
    return (
      <SigningShell>
        <Skeleton className="h-96 w-full" />
      </SigningShell>
    );
  }

  if (page.isError && sessionSigned.size === 0) {
    return (
      <SigningShell>
        <PortalNotice icon={<Clock />} tone="warn" title="This link is not valid">
          It may have been used already, or withdrawn. Ask the community for a fresh one.
        </PortalNotice>
      </SigningShell>
    );
  }

  const data = page.data;
  const exhibits = (data?.exhibits ?? []).map((exhibit) => ({
    ...exhibit,
    blocks: exhibit.blocks.map((block) => ({
      ...block,
      signed: block.signed || sessionSigned.has(blockKey(exhibit.key, block.fieldKey)),
    })),
  }));
  const blocks = exhibits.flatMap((exhibit) => exhibit.blocks);
  const outstanding = blocks.filter((block) => !block.signed).length;

  if (!data || (blocks.length > 0 && outstanding === 0 && sessionSigned.size > 0)) {
    return (
      <SigningShell
        facilityName={data?.packet.facilityName}
        residentName={data?.packet.residentName}
      >
        <PortalNotice icon={<CheckCircle2 />} tone="ok" title="Signing complete">
          Thank you. All of your signatures have been recorded, and the community has been notified.
          You may close this window.
        </PortalNotice>
      </SigningShell>
    );
  }

  const pendingExhibits = exhibits.filter((exhibit) => exhibit.blocks.some((b) => !b.signed));
  const totalAcks = pendingExhibits.reduce((n, exhibit) => n + exhibit.acknowledgments.length, 0);
  const tickedOf = (key: string) => ticked[key] ?? new Set<string>();
  const allTicked = pendingExhibits.every((exhibit) =>
    exhibit.acknowledgments.every((ack) => tickedOf(exhibit.key).has(ack.key)),
  );
  const signable = pendingExhibits
    .filter((exhibit) => !awaitsBankAccount(exhibit))
    .reduce((n, exhibit) => n + exhibit.blocks.filter((block) => !block.signed).length, 0);
  const nameValid = signerName.trim().length >= 2;
  const busy = signOne.isPending || signAll.isPending;
  const setExhibitTicks = (key: string, keys: string[]) =>
    setTicked((previous) => ({ ...previous, [key]: new Set(keys) }));

  const start = () => goTo(data.photoRequired ? "camera-intro" : "signing");

  const onCapture = (signatureDataUrl: string, photoDataUrl: string | null) => {
    if (!capture) return;
    const name = signerName.trim();
    if (capture.mode === "all") {
      signAll.mutate({ token, ...visit, signerName: name, signatureDataUrl, photoDataUrl });
      return;
    }
    signOne.mutate({
      token,
      ...visit,
      sectionKey: capture.sectionKey,
      fieldKey: capture.block.fieldKey,
      kind: capture.block.kind,
      signerName: name,
      signatureDataUrl,
      photoDataUrl,
      acknowledgedFieldKeys: [...tickedOf(capture.sectionKey)],
    });
  };

  const steps = [
    { label: "Read", done: phase !== "reading" },
    ...(data.photoRequired ? [{ label: "Camera", done: phase === "signing" }] : []),
    { label: "Confirm", done: phase === "signing" && nameValid && (totalAcks === 0 || allTicked) },
    { label: "Sign", done: false, hint: `${outstanding} left` },
  ];
  const signing = phase === "signing" || outstanding === 0;
  const camera = outstanding > 0 && (phase === "camera-intro" || phase === "camera");

  return (
    <SigningShell facilityName={data.packet.facilityName} residentName={data.packet.residentName}>
      <h1 className="sr-only">Sign your admission paperwork</h1>
      {outstanding > 0 ? <SigningSteps steps={steps} /> : null}
      <Alert className="mb-6">
        <AlertTitle>
          You are signing as {ROLE_LABELS[data.signerRole] ?? data.signerRole}
        </AlertTitle>
        <AlertDescription>
          {outstanding === 0
            ? "There is nothing left for you to sign."
            : `${outstanding} ${outstanding === 1 ? "signature" : "signatures"} still needed from you.`}{" "}
          This link is valid until {new Date(data.expiresAt).toLocaleDateString()}.
        </AlertDescription>
      </Alert>

      {outstanding > 0 && phase === "camera-intro" ? (
        <CameraIntro
          headingRef={stepHeading}
          residentName={data.packet.residentName}
          facilityName={data.packet.facilityName}
          onContinue={() => goTo("camera")}
          onBack={() => goTo("reading")}
        />
      ) : null}
      {outstanding > 0 && phase === "camera" ? (
        <CameraCheck
          headingRef={stepHeading}
          onReady={() => goTo("signing")}
          onBack={() => goTo("reading")}
        />
      ) : null}

      {outstanding > 0 && phase === "reading" ? (
        <ReadingIntro
          headingRef={stepHeading}
          forms={pendingExhibits.length}
          outstanding={outstanding}
          photoRequired={data.photoRequired}
          onStart={start}
        />
      ) : null}
      {outstanding > 0 && phase === "signing" ? (
        <SignerCard
          signerName={signerName}
          onSignerNameChange={setSignerName}
          nameValid={nameValid}
          totalAcks={totalAcks}
          allTicked={allTicked}
          onTickAll={(checked) =>
            setTicked(
              checked
                ? Object.fromEntries(
                    pendingExhibits.map((exhibit) => [
                      exhibit.key,
                      new Set(exhibit.acknowledgments.map((ack) => ack.key)),
                    ]),
                  )
                : {},
            )
          }
          outstanding={signable}
          busy={busy}
          onSignAll={() => setCapture({ mode: "all" })}
        />
      ) : null}

      {camera ? null : (
        <div className="space-y-6">
          {exhibits.map((exhibit) => (
            <ExhibitCard
              key={exhibit.key}
              exhibit={exhibit}
              signing={signing}
              ticked={tickedOf(exhibit.key)}
              onTick={(fieldKey, checked) => {
                const next = new Set(tickedOf(exhibit.key));
                if (checked) next.add(fieldKey);
                else next.delete(fieldKey);
                setExhibitTicks(exhibit.key, [...next]);
              }}
              onTickAll={(checked) =>
                setExhibitTicks(
                  exhibit.key,
                  checked ? exhibit.acknowledgments.map((ack) => ack.key) : [],
                )
              }
              canSign={nameValid && !busy}
              onSign={(block) => setCapture({ mode: "one", sectionKey: exhibit.key, block })}
              bankStep={<LinkAchBankStep token={token} signerName={signerName.trim()} />}
            />
          ))}
        </div>
      )}

      {outstanding > 0 && phase === "reading" ? <StartSigningBar onStart={start} /> : null}
      {phase === "signing" ? (
        <SigningNav
          lines={exhibits.flatMap((exhibit) =>
            exhibit.blocks.map((block) => ({
              key: blockKey(exhibit.key, block.fieldKey),
              signed: block.signed,
            })),
          )}
          acknowledged={pendingExhibits.reduce(
            (count, exhibit) =>
              count +
              exhibit.acknowledgments.filter((ack) => tickedOf(exhibit.key).has(ack.key)).length,
            0,
          )}
          acknowledgements={totalAcks}
        />
      ) : null}

      <CaptureDialog
        open={capture !== null}
        title={capture?.mode === "all" ? "Sign every form" : "Sign this form"}
        description={
          capture?.mode === "all"
            ? "One signature, applied to every line in this packet that asks for yours."
            : "Your signature is recorded with your name, the date and time."
        }
        kind={capture?.mode === "one" ? capture.block.kind : "signature"}
        photoRequired={data.photoRequired}
        pending={busy}
        onCancel={() => setCapture(null)}
        onCapture={onCapture}
      />
    </SigningShell>
  );
}
